import FileUploadTable from '@/components/file-upload-table/FileUploadTable.component';
import { CONVERSION_TYPES, DOCUMENT_TYPES } from '@/consts/uploads';
import { Conversion, ConversionStatus } from '@/models/ConversionDTO.model';
import { FeatureLevelPermissions } from '@/models/UserPermissions.model';
import ActionTableForm from '@/routes/plans/plan-detail/PlanActionTableV2/ConversionMainComponents/ActionTableForm.component';
import { PlanService } from '@/services/Plan.service';
import { userService } from '@/services/User.service';
import { Error } from '@mui/icons-material';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import { LoadingButton } from '@mui/lab';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tooltip,
  Typography
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';

import Decimal from 'decimal.js';
import React, { useCallback, useMemo, useRef } from 'react';
import { useToggle } from 'react-use';
import * as yup from 'yup';

import ConversionCardButton from './ConversionMainComponents/ConversionCardButton.component';
import ConversionDialogHeader from './ConversionMainComponents/ConversionDialogHeader.component';
import { useConversionAgGrid } from './hooks/useConversionAgGrid';
import { useConversionFile } from './hooks/useEditFile';
import useUploadOnboardingAgGridFile from './hooks/useUploadOnboardingAgGridFile.hook';

type PlanLevelFundsProps = {
  sponsorPlanId: number;
  conversion?: Conversion;
  fundMappings?: { oldCusip: string; newCusip: string }[];
  isHasActiveConversion?: boolean;
};

const validationSchema = (funds: string[]) =>
  yup
    .array()
    .of(
      yup.object().when({
        is: row => !row?.pristine,
        otherwise: schema => schema,
        then: schema =>
          schema.shape({
            amount: yup
              .number()
              .transform((_value, originalValue) =>
                Number(originalValue.replace(/,/g, ''))
              )
              .typeError('Amount must be numeric.')
              .moreThan(0, 'Amount must be greater than $0.')
              .required('Amount is required.'),
            cusip: yup.string().required('Cusip is required')
          })
      })
    )
    .test(
      'contains-all-funds',
      'All available funds must be selected at least once',
      function (array) {
        if (!funds?.length)
          return new yup.ValidationError(
            'Cusip mappings must not be empty',
            null,
            'contains-all-funds'
          );

        if (!array || array?.length === 0) return false;

        const selectedFunds = array
          .filter(i => i && !i.pristine)
          .map(item => item.cusip as string);

        const isValid = funds.every(fund => selectedFunds.includes(fund));

        return (
          isValid ||
          new yup.ValidationError(
            `All available funds must be selected at least once. Missed funds: ${funds.filter(i => !selectedFunds.includes(i)).join(', ')}`,
            null,
            'contains-all-funds'
          )
        );
      }
    );

export const PlanLevelFunds: React.FC<PlanLevelFundsProps> = ({
  sponsorPlanId,
  conversion,
  isHasActiveConversion
}) => {
  const [planActionDialogOpened, togglePlanActionDialog] = useToggle(false);
  const conversionAgGrid = useConversionAgGrid(sponsorPlanId);
  const userFileNameRef = useRef('');
  const userInfo = userService.getUser();
  const isUploadingUser = userInfo?.nickname === conversion?.submittedBy?.id;
  const isNewConversion = !conversion;
  const isDraftStage =
    conversion?.status === ConversionStatus.Draft || !conversion?.status;
  const isViewMode =
    conversion?.status && conversion.status !== ConversionStatus.Draft;

  const { mutateAsync: uploadFunds, isLoading } = useUploadOnboardingAgGridFile(
    sponsorPlanId as number,
    DOCUMENT_TYPES.PLAN_LEVEL_FUNDS,
    userFileNameRef.current
  );

  const cusipMappingsQuery = useQuery<string[]>(
    ['PlanService.getCusipMappings', sponsorPlanId],
    () =>
      PlanService.getCusipMappings(sponsorPlanId).then(d =>
        d.map(i => i.oldCusip)
      )
  );

  const columnDefs = useMemo(
    () => [
      {
        alternates: ['Cusip'],
        editable: conversion?.status !== ConversionStatus.Complete,
        field: 'cusip',
        headerName: 'Cusip',
        required: true,
        type: 'text',
        width: 200
      },
      {
        editable: true,
        field: 'amount',
        headerName: 'Amount',
        minWidth: 250,
        required: true,
        resizable: true,
        type: 'currency',
        valueParser: (value: string) => {
          if (isNaN(parseFloat(value))) return value;
          const decimalValue = new Decimal(parseFloat(value.replace(/,/g, '')));
          return decimalValue.toFixed(2);
        }
      }
    ],
    [conversion?.status]
  );

  const { onClicked: editFileClicked, isLoading: isLoadingEditFile } =
    useConversionFile(
      conversionAgGrid.handleLoad,
      sponsorPlanId,
      CONVERSION_TYPES.PLAN_LEVEL_FUNDS,
      conversion?.documentId,
      columnDefs
    );

  const rowsToDto = () =>
    conversionAgGrid.getDataForUpload({ planId: sponsorPlanId });

  const fundsError = useMemo(
    () => conversionAgGrid.form?.current?.errors?.['contains-all-funds'],
    [conversionAgGrid.form?.current?.errors]
  );

  const handleUpdate = useCallback(
    async (status: ConversionStatus) => {
      try {
        await uploadFunds({
          conversionDto: {
            ...conversion,
            context: {
              ...conversion?.context
            },
            documentId:
              conversion?.status &&
              conversion?.status !== ConversionStatus.Draft
                ? conversion.documentId
                : undefined,
            status
          },
          results: rowsToDto()
        });
      } finally {
        togglePlanActionDialog(false);
        conversionAgGrid.form.current?.setValues([]);
      }
    },
    [
      conversion,
      conversionAgGrid.form,
      rowsToDto,
      togglePlanActionDialog,
      uploadFunds
    ]
  );

  return (
    <>
      {conversion ? (
        <ConversionCardButton
          conversion={conversion}
          isLoading={isLoadingEditFile}
          onClick={() => {
            togglePlanActionDialog(true);
            editFileClicked();
          }}
          sponsorPlanId={sponsorPlanId}
        />
      ) : (
        !isHasActiveConversion && (
          <LoadingButton
            disabled={!sponsorPlanId || isLoading}
            loading={isLoading}
            onClick={() => {
              togglePlanActionDialog(true);
            }}
            startIcon={<FileUploadOutlinedIcon />}>
            Upload
          </LoadingButton>
        )
      )}
      <Dialog
        fullWidth
        maxWidth='lg'
        onClose={() => {
          togglePlanActionDialog(false);
        }}
        open={planActionDialogOpened}>
        <DialogTitle>
          <ConversionDialogHeader
            columnDefs={columnDefs}
            conversion={conversion}
            onUpload={conversionAgGrid.handleLoad}
            title='Plan Level Funds'
            viewMode={isViewMode}
          />
        </DialogTitle>
        <DialogContent>
          <ActionTableForm
            emptyRowsCount={!conversion ? 10 : undefined}
            initialValues={conversionAgGrid.gridRows}
            onChange={conversionAgGrid.setGridRows}
            onErrors={conversionAgGrid.handleErrors}
            onSubmit={conversionAgGrid.handleSubmit}
            ref={conversionAgGrid.form}
            validateOnChange
            validateOnMount
            validationSchema={validationSchema(cusipMappingsQuery.data)}>
            <FileUploadTable
              columnDefs={columnDefs}
              errors={conversionAgGrid.gridErrors}
              isLoading={isLoadingEditFile}
              isViewMode={isViewMode}
              onCellChanged={conversionAgGrid.handleCellChange}
              onRowsChanged={conversionAgGrid.handleRowsChange}
              rowData={conversionAgGrid.gridRows}
            />
          </ActionTableForm>
        </DialogContent>
        <DialogActions>
          {([ConversionStatus.PendingApproval, ConversionStatus.Draft].includes(
            conversion?.status
          ) ||
            !conversion?.status) && (
            <>
              {userService.hasPermission(
                FeatureLevelPermissions.WRITE_CONVERSION_ASSET_MAPPING_SUBMIT
              ) && (
                <LoadingButton
                  disabled={
                    conversionAgGrid.rowCount === 0 ||
                    isLoadingEditFile ||
                    (isHasActiveConversion && isNewConversion)
                  }
                  loading={isLoading}
                  onClick={async () =>
                    await handleUpdate(ConversionStatus.Draft)
                  }
                  variant='text'>
                  {isDraftStage ? 'Save as Draft' : 'Revert to Draft'}
                </LoadingButton>
              )}
              {(((isUploadingUser || isNewConversion) &&
                isDraftStage &&
                userService.hasPermission(
                  FeatureLevelPermissions.WRITE_CONVERSION_ASSET_MAPPING_SUBMIT
                )) ||
                (!isUploadingUser &&
                  userService.hasPermission(
                    FeatureLevelPermissions.WRITE_CONVERSION_ASSET_MAPPING_APPROVE
                  ))) && (
                <LoadingButton
                  disabled={
                    conversionAgGrid.errorsCount > 0 ||
                    conversionAgGrid.rowCount === 0 ||
                    isLoadingEditFile ||
                    fundsError ||
                    (isHasActiveConversion && isNewConversion)
                  }
                  loading={isLoading}
                  onClick={async () =>
                    await handleUpdate(
                      conversion?.status === ConversionStatus.Draft ||
                        !conversion?.status
                        ? ConversionStatus.PendingApproval
                        : ConversionStatus.Complete
                    )
                  }
                  variant='contained'>
                  {conversion?.status === ConversionStatus.Draft ||
                  !conversion?.status
                    ? 'Submit for approval'
                    : 'Approve & Submit'}
                </LoadingButton>
              )}
            </>
          )}
          {fundsError && (
            <Tooltip
              disableFocusListener
              title={
                <React.Fragment>
                  <Typography
                    component='pre'
                    fontSize={12}
                    whiteSpace='pre-wrap'>
                    {fundsError}
                  </Typography>
                </React.Fragment>
              }>
              <Error color='error' />
            </Tooltip>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};
